import { GameObjects, Math as PhaserMath, Scene, type Input } from 'phaser';
import type { FinishPlayAttemptInput, OrbitalBridgeQuestionResult } from '../../../../shared/results';
import {
  BRIDGE_GAME_FINISHED_EVENT,
  BRIDGE_HUD_UPDATE_EVENT,
  EventBus,
  SPECTACLE_ACTION_EVENT,
  SPECTACLE_ENTRANCE_EVENT,
  SPECTACLE_HIT_EVENT,
  type BridgeGameResult,
  type BridgeHudUpdate,
} from '../../../game/EventBus';
import { buildLearningReview } from '../../../game/domain/learningReview';
import { createLearningResultOverlay } from '../../../game/ui/LearningResultOverlay';
import type { OrbitalBridgeChoice, OrbitalBridgeConfig } from '../domain/bridgeLevels';
import { advanceBridgeTimer, advanceJourney, evaluateChoice, evaluateTimeout } from '../domain/bridgeRules';
import { bridgeChoiceFontSize, createBridgeChoiceLayout } from '../domain/choiceLayout';
import { isInDropZone, moduleDropRules, movedBeyondThreshold, type DropPoint } from '../domain/moduleDrop';
import { BridgeAudio } from './BridgeAudio';
import { createBridgeGameState, type BridgeGameState } from './BridgeGameState';

const WIDTH = 1280;
const HEIGHT = 720;
const SEGMENT_LENGTH = 920;
const FLOOR_Y = 570;
/** Yuvanın ekrandaki yüksekliği ve gaz pedalının ekran konumu (sahne koordinatı). */
const SOCKET_Y = FLOOR_Y - 14;
const PEDAL_X = 1060;
const PEDAL_Y = 575;
const CARD_Y = 300;

type ChoiceActor = {
  container: GameObjects.Container;
  correctFrame: GameObjects.Graphics;
  selectFrame: GameObjects.Graphics;
  choice: OrbitalBridgeChoice;
  index: number;
  home: DropPoint;
};

/** Basılı tutulan modül. `dragging` eşik aşılınca true olur; aşılmazsa bırakış "dokunarak seçme" sayılır. */
type HeldModule = { index: number; pointerId: number; start: DropPoint; last: DropPoint; offset: DropPoint; dragging: boolean };

export class OrbitalBridgeScene extends Scene {
  private readonly config: OrbitalBridgeConfig;
  private readonly gameId: string;
  private readonly restartEnabled: boolean;
  private readonly audio = new BridgeAudio();
  private readonly mediaTextureKeys = new Map<string, string>();
  private state!: BridgeGameState;
  private rover!: GameObjects.Container;
  private bridgePieces: GameObjects.Rectangle[] = [];
  private choiceActors: ChoiceActor[] = [];
  private promptObjects: GameObjects.GameObject[] = [];
  private feedbackText?: GameObjects.Text;
  private resultObjects: GameObjects.GameObject[] = [];
  private questionActiveMs = 0;
  private wrongChoiceCount = 0;
  private wrongChoiceIndices: number[] = [];
  private timeoutCount = 0;
  private questionLivesLost = 0;
  private questionScoreDelta = 0;
  private selectedChoiceIndex: number | null = null;
  private resultEmitted = false;
  private held: HeldModule | null = null;
  /** Dokunarak seçilmiş (yuvaya dokunmayı bekleyen) veya klavyeyle seçilmiş modül. */
  private pickedIndex: number | null = null;
  private socket?: GameObjects.Container;
  private socketRing?: GameObjects.Ellipse;
  private hintText?: GameObjects.Text;
  private pedal?: GameObjects.Container;
  private removeDomGuards?: () => void;

  constructor(config: OrbitalBridgeConfig, gameId: string, restartEnabled = true) {
    super('OrbitalBridge');
    this.config = config;
    this.gameId = gameId;
    this.restartEnabled = restartEnabled;
  }

  preload() {
    if (!this.textures.exists('orbital-bridge-rover')) {
      this.load.image('orbital-bridge-rover', '/odevlendir/assets/orbital-bridge/rover.webp');
    }
    const urls = new Set<string>();
    this.config.questions.forEach((question) => {
      if (question.promptMediaUrl) urls.add(question.promptMediaUrl);
      question.choices.forEach((choice) => { if (choice.mediaUrl) urls.add(choice.mediaUrl); });
    });
    [...urls].forEach((url, index) => {
      const key = `${this.gameId}-orbital-media-${index}`;
      this.mediaTextureKeys.set(url, key);
      if (!this.textures.exists(key)) this.load.image(key, url);
    });
  }

  create() {
    this.state = createBridgeGameState(this.config.mode, this.activeQuestion.timeLimitSeconds);
    this.resultEmitted = false;
    this.createSpaceWorld();
    this.createPlatforms();
    this.rover = this.createRover(this.state.roverX, FLOOR_Y - 63);
    this.cameras.main.setBounds(0, 0, WIDTH + this.config.questions.length * SEGMENT_LENGTH, HEIGHT);
    this.cameras.main.centerOn(this.state.cameraX, HEIGHT / 2);
    this.input.on('pointerdown', (pointer: Input.Pointer, over: GameObjects.GameObject[]) => {
      this.audio.unlock();
      if (!over.length) this.tapEmptySpace(pointer);
    });
    this.input.on('pointermove', (pointer: Input.Pointer) => this.moveHeld(pointer));
    this.input.on('pointerup', (pointer: Input.Pointer) => this.releaseHeld(pointer));
    this.input.on('pointerupoutside', (pointer: Input.Pointer) => this.releaseHeld(pointer));
    this.installTouchGuards();
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      this.audio.unlock();
      const key = event.key.toLowerCase();
      if (this.state.phase === 'awaiting-drive' && (key === 'enter' || key === ' ' || key === 'arrowright')) { event.preventDefault(); this.pressPedal(); return; }
      if (this.state.phase !== 'awaiting-answer') return;
      const index = Number(event.key) - 1;
      if (index >= 0 && index < this.choiceActors.length) { this.pickModule(index); return; }
      if (key === 'enter' || key === ' ') {
        event.preventDefault();
        if (this.pickedIndex != null) this.insertModule(this.pickedIndex);
        else this.showHint('Önce 1–5 ile bir modül seç, sonra Enter ile yuvaya tak.');
      }
    });
    this.installDebugHooks();
    this.showQuestion();
    this.refreshHud();
    EventBus.emit(SPECTACLE_ENTRANCE_EVENT, { template: 'orbital-bridge', level: 1 });
    this.time.delayedCall(850, () => {
      if (this.state.phase !== 'intro') return;
      this.state.phase = 'awaiting-answer';
      this.audio.play('start');
      this.refreshHud();
    });
    EventBus.emit('current-scene-ready', this);
  }

  update(_time: number, delta: number) {
    this.simulate(Math.min(delta, 80));
  }

  private get activeQuestion() {
    return this.config.questions[this.state?.questionIndex ?? 0];
  }

  private simulate(deltaMs: number) {
    if (this.state.phase !== 'awaiting-answer') return;
    this.questionActiveMs += deltaMs;
    if (this.state.remainingMs == null) return;
    const timer = advanceBridgeTimer(this.state.phase, this.state.remainingMs, deltaMs);
    this.state.remainingMs = timer.remainingMs;
    this.refreshHud();
    if (timer.timedOut) this.handleTimeout();
  }

  private createSpaceWorld() {
    const worldWidth = WIDTH + this.config.questions.length * SEGMENT_LENGTH;
    this.add.rectangle(worldWidth / 2, HEIGHT / 2, worldWidth, HEIGHT, 0x061126).setDepth(-10);
    const far = this.add.graphics().setDepth(-9).setScrollFactor(0.12);
    const near = this.add.graphics().setDepth(-8).setScrollFactor(0.32);
    const random = new PhaserMath.RandomDataGenerator([this.config.id, 'orbital-bridge']);
    for (let index = 0; index < 110; index += 1) {
      const x = random.between(0, WIDTH * 2);
      const y = random.between(20, 520);
      far.fillStyle(index % 5 === 0 ? 0x8eefff : 0xffffff, random.realInRange(0.25, 0.8));
      far.fillCircle(x, y, random.realInRange(1, 2.6));
    }
    for (let index = 0; index < 28; index += 1) {
      near.fillStyle(index % 2 ? 0x6f5bd7 : 0x36bfd0, 0.12);
      near.fillCircle(random.between(0, WIDTH * 2), random.between(40, 500), random.between(12, 48));
    }
    this.add.circle(1050, 460, 170, 0x5d2c91, 0.72).setDepth(-7).setScrollFactor(0.18);
    this.add.circle(1000, 405, 68, 0xee8b54, 0.45).setDepth(-6).setScrollFactor(0.18);
  }

  private createPlatforms() {
    const graphics = this.add.graphics().setDepth(1);
    const worldWidth = WIDTH + this.config.questions.length * SEGMENT_LENGTH;
    graphics.fillStyle(0x102c4c, 1);
    graphics.fillRect(0, FLOOR_Y, worldWidth, 110);
    graphics.fillStyle(0x1f5470, 1);
    graphics.fillRect(0, FLOOR_Y, worldWidth, 18);
    graphics.lineStyle(3, 0x48d9f2, 0.75);
    graphics.lineBetween(0, FLOOR_Y + 2, worldWidth, FLOOR_Y + 2);
    for (let index = 0; index < this.config.questions.length; index += 1) {
      const gapCenter = 680 + index * SEGMENT_LENGTH;
      graphics.fillStyle(0x061126, 1);
      graphics.fillRect(gapCenter - 145, FLOOR_Y - 2, 290, 120);
      graphics.lineStyle(4, 0xffc85d, 0.75);
      graphics.lineBetween(gapCenter - 145, FLOOR_Y, gapCenter - 145, FLOOR_Y + 58);
      graphics.lineBetween(gapCenter + 145, FLOOR_Y, gapCenter + 145, FLOOR_Y + 58);
      this.add.circle(gapCenter - 145, FLOOR_Y + 8, 10, 0x4ce5f2, 0.85).setDepth(2);
      this.add.circle(gapCenter + 145, FLOOR_Y + 8, 10, 0x4ce5f2, 0.85).setDepth(2);
    }
    for (let x = 80; x < worldWidth; x += 155) {
      this.add.rectangle(x, FLOOR_Y + 62, 84, 18, 0x081a31, 0.9).setDepth(2);
      this.add.circle(x - 29, FLOOR_Y + 63, 6, 0x57d8e7, 0.55).setDepth(3);
      this.add.circle(x + 29, FLOOR_Y + 63, 6, 0x57d8e7, 0.55).setDepth(3);
    }
  }

  private createRover(x: number, y: number): GameObjects.Container {
    const container = this.add.container(x, y).setDepth(8);
    const glow = this.add.ellipse(0, 42, 170, 28, 0x42ddff, 0.14);
    if (this.textures.exists('orbital-bridge-rover')) {
      const shadow = this.add.ellipse(0, 53, 190, 22, 0x000000, 0.34);
      const image = this.add.image(0, 60, 'orbital-bridge-rover').setOrigin(0.5, 1).setDisplaySize(230, 153);
      container.add([glow, shadow, image]);
      this.tweens.add({ targets: glow, scaleX: 1.18, alpha: 0.07, duration: 680, yoyo: true, repeat: -1 });
      return container;
    }
    const track = this.add.rectangle(0, 34, 148, 34, 0x121b2b).setStrokeStyle(4, 0x557594);
    const body = this.add.polygon(0, -5, [-70, 20, -52, -30, 36, -30, 70, 5, 58, 28, -58, 28], 0xe28d3e)
      .setStrokeStyle(4, 0xffd269);
    const cabin = this.add.rectangle(-8, -49, 72, 48, 0x153c59).setStrokeStyle(4, 0x5ce1f1);
    const window = this.add.rectangle(-8, -50, 45, 27, 0x8debf3, 0.72);
    const antenna = this.add.line(0, 0, 27, -74, 38, -105, 0x86eef5).setLineWidth(3);
    const beacon = this.add.circle(39, -105, 7, 0xffd45f);
    const wheels = [-49, -17, 17, 49].map((offset) => this.add.circle(offset, 35, 14, 0x0a1424).setStrokeStyle(4, 0xf3b74f));
    const headlight = this.add.circle(64, -4, 8, 0xffef91).setBlendMode('ADD');
    container.add([glow, track, body, cabin, window, antenna, beacon, ...wheels, headlight]);
    this.tweens.add({ targets: beacon, alpha: 0.28, duration: 480, yoyo: true, repeat: -1 });
    return container;
  }

  private showQuestion() {
    this.clearQuestionObjects();
    const question = this.activeQuestion;
    const panel = this.add.rectangle(WIDTH / 2, 98, 1030, 150, 0x091d35, 0.94)
      .setStrokeStyle(3, 0x49dceb, 0.85).setScrollFactor(0).setDepth(30);
    const title = this.add.text(158, 39, `YÖRÜNGE ${question.id} · KÖPRÜ BOŞLUĞU`, {
      fontFamily: 'system-ui, sans-serif', fontSize: '18px', fontStyle: 'bold', color: '#72e8f5',
    }).setScrollFactor(0).setDepth(31);
    this.promptObjects.push(panel, title);
    let promptX = WIDTH / 2;
    let promptWidth = 820;
    if (question.promptMediaUrl) {
      const key = this.mediaTextureKeys.get(question.promptMediaUrl);
      if (key) {
        const image = this.add.image(question.prompt ? 240 : WIDTH / 2, 108, key).setScrollFactor(0).setDepth(31);
        image.setDisplaySize(question.prompt ? 112 : 190, question.prompt ? 82 : 112);
        this.promptObjects.push(image);
        promptX = 710;
        promptWidth = 700;
      }
    }
    if (question.prompt) {
      const prompt = this.add.text(promptX, 104, question.prompt, {
        fontFamily: 'system-ui, sans-serif', fontSize: question.prompt.length > 120 ? '23px' : question.prompt.length > 70 ? '27px' : '32px',
        fontStyle: 'bold', color: '#f4f7ef', align: 'center', wordWrap: { width: promptWidth },
      }).setOrigin(0.5).setScrollFactor(0).setDepth(31);
      this.promptObjects.push(prompt);
    }
    this.createChoices();
    this.createBridgePieces();
  }

  private createChoices() {
    const choices = this.activeQuestion.choices;
    const layout = createBridgeChoiceLayout(choices.length, WIDTH / 2);
    choices.forEach((choice, index) => {
      const x = layout.centers[index];
      const y = CARD_Y;
      const container = this.add.container(x, y).setScrollFactor(0).setDepth(32);
      const accent = index % 2 ? 0xb69aff : 0x63e9ef;
      const cardWidth = layout.cardWidth;
      const cardHeight = layout.cardHeight;
      const shadow = this.add.graphics();
      shadow.fillStyle(0x000000, 0.34);
      shadow.fillRoundedRect(-cardWidth / 2 + 6, -cardHeight / 2 + 8, cardWidth, cardHeight, 18);
      const card = this.add.graphics();
      card.fillStyle(0x0b2440, 0.98);
      card.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
      card.lineStyle(3, accent, 0.95);
      card.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
      card.fillStyle(accent, 0.92);
      card.fillRoundedRect(-cardWidth / 2 + 12, -cardHeight / 2 + 10, cardWidth - 24, 6, 3);
      const selectFrame = this.add.graphics().setVisible(false);
      selectFrame.lineStyle(5, 0xffd45f, 1);
      selectFrame.strokeRoundedRect(-cardWidth / 2 - 5, -cardHeight / 2 - 5, cardWidth + 10, cardHeight + 10, 22);
      const correctFrame = this.add.graphics().setVisible(false);
      correctFrame.lineStyle(6, 0x78f5c4, 1);
      correctFrame.strokeRoundedRect(-cardWidth / 2 - 5, -cardHeight / 2 - 5, cardWidth + 10, cardHeight + 10, 22);
      const shortcutBadge = this.add.circle(-cardWidth / 2 + 27, -cardHeight / 2 + 31, 17, accent, 1)
        .setStrokeStyle(2, 0xf4f7ef, 0.9);
      const shortcutText = this.add.text(shortcutBadge.x, shortcutBadge.y, String(index + 1), {
        fontFamily: 'system-ui, sans-serif', fontSize: '17px', fontStyle: 'bold', color: '#061126',
      }).setOrigin(0.5);
      container.add([shadow, selectFrame, correctFrame, card, shortcutBadge, shortcutText]);

      const hasLabel = Boolean(choice.label);
      const hasMedia = Boolean(choice.mediaUrl);
      const horizontalMediaLayout = hasMedia && hasLabel && cardWidth >= 280;
      let textX = 0;
      let textY = 12;
      let textWidth = cardWidth - 34;
      if (choice.mediaUrl) {
        const key = this.mediaTextureKeys.get(choice.mediaUrl);
        if (key) {
          const image = this.add.image(
            horizontalMediaLayout ? -cardWidth / 2 + 76 : 0,
            hasLabel && !horizontalMediaLayout ? -18 : 10,
            key,
          );
          image.setDisplaySize(
            hasLabel ? (horizontalMediaLayout ? 82 : 58) : Math.min(120, cardWidth - 58),
            hasLabel ? (horizontalMediaLayout ? 76 : 48) : 84,
          );
          container.add(image);
          if (horizontalMediaLayout) {
            textX = 52;
            textY = 12;
            textWidth = cardWidth - 126;
          } else if (hasLabel) {
            textY = 36;
            textWidth = cardWidth - 30;
          }
        }
      }
      if (choice.label) {
        container.add(this.add.text(textX, textY, choice.label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: `${bridgeChoiceFontSize(choice.label.length, cardWidth, hasMedia)}px`,
          fontStyle: 'bold', color: '#f5f8f2', align: 'center', wordWrap: { width: textWidth, useAdvancedWrap: true },
          lineSpacing: 2,
        }).setOrigin(0.5));
      }
      container.setSize(cardWidth, cardHeight).setInteractive({ useHandCursor: true });
      container.on('pointerdown', (pointer: Input.Pointer) => this.holdModule(index, pointer));
      container.on('pointerover', () => { if (this.state.phase === 'awaiting-answer' && !this.held) container.setScale(1.035); });
      container.on('pointerout', () => { if (!this.held && this.pickedIndex !== index) container.setScale(1); });
      this.choiceActors.push({ container, correctFrame, selectFrame, choice, index, home: { x, y } });
      this.tweens.add({ targets: shortcutBadge, alpha: 0.58, duration: 760 + index * 80, yoyo: true, repeat: -1 });
    });
  }

  private createBridgePieces() {
    this.bridgePieces.forEach((piece) => piece.destroy());
    this.bridgePieces = [];
    const center = 680 + this.state.questionIndex * SEGMENT_LENGTH;
    this.createSocket(center);
    for (let index = 0; index < 7; index += 1) {
      const piece = this.add.rectangle(center - 126 + index * 42, FLOOR_Y + 2, 38, 22, 0x53dcea)
        .setStrokeStyle(3, 0xffd269).setDepth(5).setAlpha(0).setScale(0.2);
      this.bridgePieces.push(piece);
    }
  }

  private moduleUsable(index: number) {
    const actor = this.choiceActors[index];
    return this.state.phase === 'awaiting-answer' && Boolean(actor) && actor.container.alpha >= 0.5;
  }

  /** Köprü boşluğundaki enerji yuvası (dünya koordinatı). Doğruluk bilgisi göstermez; yalnız "buraya tak" hedefidir. */
  private createSocket(centerX: number) {
    this.socket?.destroy();
    const socket = this.add.container(centerX, SOCKET_Y).setDepth(6);
    const glow = this.add.ellipse(0, 0, 250, 70, 0x49dceb, 0.12);
    const ring = this.add.ellipse(0, 0, 210, 46).setStrokeStyle(4, 0x49dceb, 0.95);
    const label = this.add.text(0, -52, 'ENERJİ YUVASI', {
      fontFamily: 'system-ui, sans-serif', fontSize: '18px', fontStyle: 'bold', color: '#9ff4fb', backgroundColor: '#07182dcc', padding: { x: 10, y: 4 },
    }).setOrigin(0.5);
    socket.add([glow, ring, label]);
    this.tweens.add({ targets: glow, scaleX: 1.15, alpha: 0.28, duration: 720, yoyo: true, repeat: -1 });
    this.socket = socket; this.socketRing = ring;
  }

  private socketScreenX() {
    return (this.socket?.x ?? 680) - this.cameras.main.scrollX;
  }

  private setSocketActive(active: boolean) {
    this.socketRing?.setStrokeStyle(active ? 6 : 4, active ? 0xffd45f : 0x49dceb, 1);
    this.socket?.setScale(active ? 1.08 : 1);
  }

  private showHint(message: string) {
    this.hintText?.destroy();
    this.hintText = this.add.text(WIDTH / 2, 412, message, {
      fontFamily: 'system-ui, sans-serif', fontSize: '22px', fontStyle: 'bold', color: '#ffe8a3', backgroundColor: '#07182de6', padding: { x: 18, y: 9 },
      align: 'center', wordWrap: { width: 900 },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(41);
  }

  private clearHint() { this.hintText?.destroy(); this.hintText = undefined; }

  private holdModule(index: number, pointer: Input.Pointer) {
    if (!this.moduleUsable(index) || this.held) return;
    const actor = this.choiceActors[index];
    const point = { x: pointer.x, y: pointer.y };
    this.held = { index, pointerId: pointer.id, start: point, last: point, offset: { x: actor.container.x - point.x, y: actor.container.y - point.y }, dragging: false };
    actor.container.setDepth(44);
  }

  private moveHeld(pointer: Input.Pointer) {
    const held = this.held;
    if (!held || pointer.id !== held.pointerId || !this.moduleUsable(held.index)) return;
    held.last = { x: pointer.x, y: pointer.y };
    if (!held.dragging && movedBeyondThreshold(held.start, held.last)) {
      held.dragging = true;
      if (this.pickedIndex != null && this.pickedIndex !== held.index) this.returnHome(this.pickedIndex);
      this.pickedIndex = null;
      this.choiceActors[held.index].selectFrame.setVisible(true);
      this.choiceActors[held.index].container.setScale(1.06);
      this.clearHint();
    }
    if (!held.dragging) return;
    this.choiceActors[held.index].container.setPosition(held.last.x + held.offset.x, held.last.y + held.offset.y);
    this.setSocketActive(isInDropZone(held.last, this.socketScreenX()));
  }

  private releaseHeld(pointer: Input.Pointer | null) {
    const held = this.held;
    if (!held || (pointer && pointer.id !== held.pointerId)) return;
    if (pointer) held.last = { x: pointer.x, y: pointer.y };
    this.held = null;
    this.setSocketActive(false);
    if (!this.moduleUsable(held.index)) return;
    if (held.dragging) {
      if (isInDropZone(held.last, this.socketScreenX())) this.insertModule(held.index);
      else { this.returnHome(held.index); this.showHint('Modülü köprüdeki enerji yuvasına taşı.'); }
      return;
    }
    // Hareketsiz basış: akıllı tahtada sürükleme olayları gelmese bile dokun-seç, sonra yuvaya dokun.
    this.pickModule(held.index);
  }

  private pickModule(index: number) {
    if (!this.moduleUsable(index)) return;
    if (this.pickedIndex != null && this.pickedIndex !== index) this.returnHome(this.pickedIndex);
    this.pickedIndex = index;
    const actor = this.choiceActors[index];
    actor.selectFrame.setVisible(true);
    actor.container.setDepth(44);
    this.tweens.add({ targets: actor.container, x: actor.home.x, y: actor.home.y - 20, scale: 1.06, duration: 160, ease: 'Sine.Out' });
    this.audio.play('select');
    this.showHint('Modül seçildi · Şimdi köprüdeki enerji yuvasına sürükle ya da yuvaya dokun.');
    this.setSocketActive(true);
  }

  private tapEmptySpace(pointer: Input.Pointer) {
    if (this.state.phase !== 'awaiting-answer' || this.pickedIndex == null || this.held) return;
    if (isInDropZone({ x: pointer.x, y: pointer.y }, this.socketScreenX())) this.insertModule(this.pickedIndex);
  }

  private returnHome(index: number) {
    const actor = this.choiceActors[index];
    if (!actor) return;
    actor.selectFrame.setVisible(false);
    if (this.pickedIndex === index) { this.pickedIndex = null; this.setSocketActive(false); }
    this.tweens.add({ targets: actor.container, x: actor.home.x, y: actor.home.y, scale: 1, duration: 240, ease: 'Sine.Out', onComplete: () => actor.container.setDepth(32) });
  }

  /** Modülü yuvaya uçurur ve cevabı değerlendirir. Yanlış modül kıvılcımla yerine seker. */
  private insertModule(index: number) {
    if (!this.moduleUsable(index)) return;
    const actor = this.choiceActors[index];
    this.state.phase = 'feedback';
    this.pickedIndex = null; this.held = null;
    actor.selectFrame.setVisible(false);
    this.clearHint(); this.setSocketActive(false);
    actor.container.setDepth(44);
    this.audio.play('select');
    this.tweens.add({
      targets: actor.container, x: this.socketScreenX(), y: SOCKET_Y - 30, scale: 0.55, duration: 300, ease: 'Sine.In',
      onComplete: () => {
        if (actor.choice.correct) {
          actor.container.setVisible(false);
          this.flashSocket(0x78f5c4);
        } else {
          this.sparkSocket();
          this.tweens.add({ targets: actor.container, x: actor.home.x, y: actor.home.y, scale: 1, duration: 380, ease: 'Back.Out', onComplete: () => actor.container.setDepth(32) });
        }
        this.applyEvaluation(evaluateChoice(this.state, actor.choice.correct), index);
      },
    });
  }

  private flashSocket(color: number) {
    this.socketRing?.setStrokeStyle(8, color, 1);
    this.tweens.add({ targets: this.socket, scale: 1.18, duration: 180, yoyo: true });
  }

  private sparkSocket() {
    const x = this.socket?.x ?? 680;
    for (let index = 0; index < 14; index += 1) {
      const angle = (index / 14) * Math.PI * 2;
      const spark = this.add.circle(x, SOCKET_Y, 5, index % 2 ? 0xffd45f : 0xff8d7d, 1).setDepth(12);
      this.tweens.add({ targets: spark, x: x + Math.cos(angle) * 90, y: SOCKET_Y + Math.sin(angle) * 55, alpha: 0, scale: 0.3, duration: 420, onComplete: () => spark.destroy() });
    }
    this.cameras.main.shake(180, 0.006);
  }

  /** Rehberli modda veya süre dolunca doğru modül animasyonla gösterilip kendiliğinden takılır. */
  private autoInsertCorrect(then: () => void) {
    const correct = this.choiceActors.find((actor) => actor.choice.correct);
    if (!correct) { then(); return; }
    correct.container.setDepth(44);
    this.tweens.add({
      targets: correct.container, x: this.socketScreenX(), y: SOCKET_Y - 30, scale: 0.55, duration: 520, ease: 'Sine.InOut',
      onComplete: () => { correct.container.setVisible(false); this.flashSocket(0x78f5c4); then(); },
    });
  }

  private showPedal() {
    this.state.phase = 'awaiting-drive';
    this.feedbackText?.setText('KÖPRÜ HAZIR! GAZ PEDALINA BAS');
    const pedal = this.add.container(PEDAL_X, PEDAL_Y).setScrollFactor(0).setDepth(46);
    const glow = this.add.circle(0, 0, 96, 0x78f5c4, 0.2);
    const base = this.add.circle(0, 0, 76, 0x0b2440, 1).setStrokeStyle(6, 0x78f5c4, 1);
    const plate = this.add.graphics();
    plate.fillStyle(0xd9e5ec, 1); plate.fillRoundedRect(-30, -46, 60, 86, 14);
    plate.lineStyle(4, 0x7b8d99, 1);
    for (let row = -30; row <= 24; row += 14) plate.lineBetween(-18, row, 18, row);
    const label = this.add.text(0, 104, 'GAZ!', { fontFamily: 'system-ui, sans-serif', fontSize: '30px', fontStyle: 'bold', color: '#78f5c4', backgroundColor: '#07182de6', padding: { x: 14, y: 4 } }).setOrigin(0.5);
    pedal.add([glow, base, plate, label]);
    // Geniş dokunma alanı: akıllı tahta ve küçük ekranda da kolay basılır.
    pedal.setSize(230, 260).setInteractive({ useHandCursor: true });
    pedal.on('pointerdown', () => this.pressPedal());
    pedal.on('pointerup', () => this.pressPedal());
    this.tweens.add({ targets: glow, scale: 1.25, alpha: 0.05, duration: 620, yoyo: true, repeat: -1 });
    this.pedal = pedal;
    this.refreshHud();
    this.time.delayedCall(moduleDropRules.pedalHintMs, () => {
      if (this.state.phase !== 'awaiting-drive' || this.pedal !== pedal) return;
      this.showHint('Aracı ilerletmek için sağdaki GAZ pedalına bas!');
      this.tweens.add({ targets: pedal, scale: 1.12, duration: 380, yoyo: true, repeat: -1 });
    });
  }

  private pressPedal() {
    if (this.state.phase !== 'awaiting-drive') return;
    const pedal = this.pedal; this.pedal = undefined;
    this.clearHint();
    if (pedal) {
      pedal.disableInteractive();
      this.tweens.killTweensOf(pedal);
      this.tweens.add({ targets: pedal, scaleY: 0.82, duration: 90, yoyo: true, onComplete: () => this.tweens.add({ targets: pedal, alpha: 0, duration: 260, onComplete: () => pedal.destroy() }) });
    }
    this.driveAcross('correct');
  }

  /** Akıllı tahta güvenliği: uzun basış menüsünü, tarayıcı kaydırmasını ve iptal edilen dokunuşu yönetir. */
  private installTouchGuards() {
    const canvas = this.game.canvas;
    canvas.style.touchAction = 'none';
    canvas.style.userSelect = 'none';
    canvas.style.setProperty('-webkit-touch-callout', 'none');
    const preventMenu = (event: Event) => event.preventDefault();
    const cancel = () => { if (this.held) this.releaseHeld(null); };
    canvas.addEventListener('contextmenu', preventMenu);
    canvas.addEventListener('pointercancel', cancel);
    canvas.addEventListener('touchcancel', cancel);
    this.removeDomGuards?.();
    this.removeDomGuards = () => {
      canvas.removeEventListener('contextmenu', preventMenu);
      canvas.removeEventListener('pointercancel', cancel);
      canvas.removeEventListener('touchcancel', cancel);
    };
    this.events.once('shutdown', () => { this.removeDomGuards?.(); this.removeDomGuards = undefined; });
  }

  private handleTimeout() {
    if (this.state.phase !== 'awaiting-answer') return;
    if (this.held) { const index = this.held.index; this.held = null; this.returnHome(index); }
    if (this.pickedIndex != null) this.returnHome(this.pickedIndex);
    this.clearHint();
    this.audio.play('timeout');
    this.timeoutCount += 1;
    this.applyEvaluation(evaluateTimeout(this.state), null);
  }

  private applyEvaluation(evaluation: ReturnType<typeof evaluateChoice>, selectedIndex: number | null) {
    this.state.phase = 'feedback';
    this.state.score = evaluation.score;
    this.state.lives = evaluation.lives;
    this.state.streak = evaluation.streak;
    this.state.correct += evaluation.correctDelta;
    this.state.wrong += evaluation.wrongDelta;
    this.state.attempts += evaluation.attemptsDelta;
    this.questionLivesLost += evaluation.livesLost;
    this.questionScoreDelta += evaluation.scoreDelta;
    this.selectedChoiceIndex = selectedIndex;
    if (evaluation.outcome === 'wrong') {
      this.wrongChoiceCount += 1;
      if (selectedIndex != null && !this.wrongChoiceIndices.includes(selectedIndex)) this.wrongChoiceIndices.push(selectedIndex);
    }
    if (selectedIndex != null && !this.choiceActors[selectedIndex].choice.correct) {
      this.choiceActors[selectedIndex].container.setAlpha(0.28).disableInteractive();
    }
    if (evaluation.showCorrectChoice) this.highlightCorrectChoice();
    const message = evaluation.outcome === 'correct' ? 'ENERJİ UYUMLU!' : evaluation.outcome === 'timeout' ? 'SÜRE DOLDU!' : 'MODÜL UYUMSUZ!';
    const color = evaluation.outcome === 'correct' ? '#78f5c4' : '#ff9b8f';
    this.feedbackText?.destroy();
    this.feedbackText = this.add.text(WIDTH / 2, 405, message, {
      fontFamily: 'system-ui, sans-serif', fontSize: '35px', fontStyle: 'bold', color,
      backgroundColor: '#07182de8', padding: { x: 25, y: 13 },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(40);
    evaluation.outcome === 'correct' ? this.audio.play('build') : this.audio.play('wrong');
    EventBus.emit(evaluation.outcome === 'correct' ? SPECTACLE_ACTION_EVENT : SPECTACLE_HIT_EVENT, {
      template: 'orbital-bridge', question: this.state.questionIndex + 1, outcome: evaluation.outcome,
    });
    this.refreshHud();

    if (evaluation.shouldBuildBridge) {
      if (evaluation.outcome === 'correct') this.time.delayedCall(620, () => this.buildBridge('correct'));
      else this.time.delayedCall(1_250, () => this.autoInsertCorrect(() => this.buildBridge(evaluation.outcome)));
      return;
    }
    if (!evaluation.shouldStayOnQuestion) {
      this.time.delayedCall(900, () => {
        this.recordQuestion(evaluation.outcome, false);
        this.finish(false);
      });
      return;
    }
    this.time.delayedCall(850, () => {
      this.feedbackText?.destroy();
      this.feedbackText = undefined;
      this.state.phase = 'awaiting-answer';
      if (evaluation.outcome === 'timeout') {
        this.state.remainingMs = this.activeQuestion.timeLimitSeconds == null ? null : this.activeQuestion.timeLimitSeconds * 1000;
      }
      this.refreshHud();
    });
  }

  private highlightCorrectChoice() {
    const correct = this.choiceActors.find((actor) => actor.choice.correct);
    if (!correct) return;
    correct.correctFrame.setVisible(true);
    correct.container.setAlpha(1).setScale(1.08);
    this.tweens.add({ targets: correct.container, scale: 1.16, duration: 260, yoyo: true, repeat: 2 });
  }

  private buildBridge(outcome: 'correct' | 'wrong' | 'timeout') {
    this.state.phase = 'bridge-building';
    this.feedbackText?.setText(outcome === 'correct' ? 'KÖPRÜ KURULUYOR' : 'DOĞRU MODÜL DEVREDE');
    this.bridgePieces.forEach((piece, index) => {
      this.tweens.add({ targets: piece, alpha: 1, scale: 1, duration: 210, delay: index * 95, ease: 'Back.Out' });
    });
    this.refreshHud();
    // Doğru cevapta aracı öğrenci gaz pedalıyla ilerletir; yanlış/süre dolduğunda araç kendiliğinden geçer.
    this.time.delayedCall(950, () => (outcome === 'correct' ? this.showPedal() : this.driveAcross(outcome)));
  }

  private driveAcross(outcome: 'correct' | 'wrong' | 'timeout') {
    this.state.phase = 'rover-crossing';
    this.audio.play('drive');
    const journey = advanceJourney({ segment: this.state.segment, roverX: this.state.roverX, cameraX: this.state.cameraX });
    const wheels = this.rover.list.filter((child) => child instanceof GameObjects.Arc);
    wheels.forEach((wheel) => this.tweens.add({ targets: wheel, angle: 720, duration: 1_350 }));
    this.tweens.add({
      targets: this.rover,
      x: journey.roverX,
      duration: 1_350,
      ease: 'Sine.InOut',
      onUpdate: () => { this.rover.y = FLOOR_Y - 63 + Math.sin(this.rover.x * 0.04) * 3; },
      onComplete: () => {
        this.rover.y = FLOOR_Y - 63;
        this.state.segment = journey.segment;
        this.state.roverX = journey.roverX;
        this.recordQuestion(outcome, true);
        if (this.state.questionIndex >= this.config.questions.length - 1) {
          this.finish(true);
          return;
        }
        this.travelCamera(journey.cameraX);
      },
    });
    this.refreshHud();
  }

  private travelCamera(cameraX: number) {
    this.state.phase = 'camera-travel';
    this.state.cameraX = cameraX;
    this.state.questionIndex += 1;
    this.tweens.addCounter({
      from: this.cameras.main.midPoint.x,
      to: cameraX,
      duration: 900,
      ease: 'Sine.InOut',
      onUpdate: (tween) => this.cameras.main.centerOn(tween.getValue() ?? cameraX, HEIGHT / 2),
      onComplete: () => {
        this.resetQuestionCounters();
        this.state.remainingMs = this.activeQuestion.timeLimitSeconds == null ? null : this.activeQuestion.timeLimitSeconds * 1000;
        this.state.phase = 'awaiting-answer';
        this.showQuestion();
        this.refreshHud();
      },
    });
    this.refreshHud();
  }

  private recordQuestion(outcome: 'correct' | 'wrong' | 'timeout', completed: boolean) {
    const detail: OrbitalBridgeQuestionResult = {
      level: this.state.questionIndex + 1,
      durationMs: Math.round(this.questionActiveMs),
      outcome,
      selectedChoiceIndex: this.selectedChoiceIndex,
      wrongChoiceCount: this.wrongChoiceCount,
      timeoutCount: this.timeoutCount,
      livesLost: this.questionLivesLost,
      scoreDelta: this.questionScoreDelta,
      completed,
      correctChoiceIndex: this.activeQuestion.choices.findIndex((choice) => choice.correct),
      wrongChoiceIndices: [...this.wrongChoiceIndices],
    };
    this.state.questionResults.push(detail);
  }

  private resetQuestionCounters() {
    this.questionActiveMs = 0;
    this.wrongChoiceCount = 0;
    this.wrongChoiceIndices = [];
    this.timeoutCount = 0;
    this.questionLivesLost = 0;
    this.questionScoreDelta = 0;
    this.selectedChoiceIndex = null;
    this.feedbackText?.destroy();
    this.feedbackText = undefined;
  }

  private finish(completed: boolean) {
    this.state.phase = completed ? 'finished' : 'game-over';
    completed ? this.audio.play('complete') : this.audio.play('game-over');
    this.clearQuestionObjects();
    this.showResult(completed);
    this.refreshHud();
    const result: FinishPlayAttemptInput = {
      score: this.state.score,
      lives: this.state.lives,
      correct: this.state.correct,
      wrong: this.state.wrong,
      attempts: this.state.attempts,
      completed,
      details: {
        templateKey: 'orbital-bridge',
        mode: this.config.mode,
        questions: this.state.questionResults,
      },
    };
    if (!this.resultEmitted) {
      this.resultEmitted = true;
      EventBus.emit(BRIDGE_GAME_FINISHED_EVENT, { gameId: this.gameId, result } satisfies BridgeGameResult);
    }
  }

  private showResult(completed: boolean) {
    const reviewQuestions = this.config.questions.map((question) => ({
      prompt: question.prompt,
      promptMediaUrl: question.promptMediaUrl,
      choices: question.choices.map((choice) => ({ text: choice.label, mediaUrl: choice.mediaUrl, correct: choice.correct })),
    }));
    const reviewItems = buildLearningReview(reviewQuestions, this.state.questionResults.map((result) => ({
      label: `${result.level}. soru`,
      questionIndex: result.level - 1,
      wrongChoiceIndices: result.wrongChoiceIndices,
      wrongCount: result.wrongChoiceCount,
      timeoutCount: result.timeoutCount,
      completed: result.completed,
      completionStatus: !result.completed
        ? 'incomplete'
        : result.outcome === 'correct'
          ? 'completed-after-mistake'
          : 'answer-shown',
    })));
    const overlay = createLearningResultOverlay(this, {
      centerX: WIDTH / 2,
      centerY: HEIGHT / 2,
      viewportWidth: WIDTH,
      viewportHeight: HEIGHT,
      title: completed ? 'YÖRÜNGE KÖPRÜSÜ TAMAMLANDI!' : 'KEŞİF ARACI DURDU',
      titleColor: completed ? '#7cf2c8' : '#ff9d8f',
      accentColor: completed ? 0x6ee8c0 : 0xff8d7d,
      summaryLines: [
        `${this.state.score} puan · ${this.state.correct}/${this.config.questions.length} doğru`,
        `${this.state.wrong} yanlış/zaman aşımı · ${this.state.segment} köprü geçildi`,
      ],
      reviewItems,
      restartEnabled: this.restartEnabled,
      restartLabel: 'Yeniden oyna',
      onRestart: () => this.scene.restart(),
      resolveTextureKey: (mediaUrl) => this.mediaTextureKeys.get(mediaUrl),
    }).setScrollFactor(0);
    this.resultObjects.push(overlay);
  }

  private clearQuestionObjects() {
    this.held = null; this.pickedIndex = null; this.clearHint();
    this.pedal?.destroy(); this.pedal = undefined;
    this.promptObjects.forEach((object) => object.destroy());
    this.choiceActors.forEach((actor) => actor.container.destroy());
    this.feedbackText?.destroy();
    this.promptObjects = [];
    this.choiceActors = [];
    this.feedbackText = undefined;
  }

  private refreshHud() {
    EventBus.emit(BRIDGE_HUD_UPDATE_EVENT, {
      gameId: this.gameId,
      hud: {
        question: this.state.questionIndex + 1,
        totalQuestions: this.config.questions.length,
        phase: this.state.phase,
        mode: this.config.mode,
        score: this.state.score,
        lives: this.state.lives,
        remainingMs: this.state.remainingMs,
      },
    } satisfies BridgeHudUpdate);
  }

  private installDebugHooks() {
    window.render_game_to_text = () => JSON.stringify({
      template: 'orbital-bridge', mode: this.config.mode, phase: this.state.phase,
      question: this.state.questionIndex + 1, totalQuestions: this.config.questions.length,
      score: this.state.score, lives: this.state.lives, remainingMs: this.state.remainingMs,
      journey: { segment: this.state.segment, roverX: Math.round(this.rover.x), cameraX: Math.round(this.cameras.main.midPoint.x) },
      options: this.activeQuestion?.choices.map((choice, index) => ({ index: index + 1, text: choice.label, correct: choice.correct })),
      // Sahne koordinatları (1280×720): tarayıcı testleri sürüklemeyi gerçek pointer olaylarıyla yapar.
      modules: this.choiceActors.map((actor) => ({ index: actor.index + 1, x: Math.round(actor.container.x), y: Math.round(actor.container.y), usable: this.moduleUsable(actor.index) })),
      socket: { x: Math.round(this.socketScreenX()), y: SOCKET_Y },
      picked: this.pickedIndex == null ? null : this.pickedIndex + 1,
      dragging: Boolean(this.held?.dragging),
      pedal: this.pedal ? { x: PEDAL_X, y: PEDAL_Y } : null,
      hint: this.hintText?.text ?? null,
    });
    window.advanceTime = (milliseconds: number) => {
      let remaining = Math.max(0, milliseconds);
      while (remaining > 0) {
        const step = Math.min(50, remaining);
        this.simulate(step);
        remaining -= step;
      }
    };
  }
}
